import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { Timer } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { requestGetRunningTimer } from '../redux/sagas/requests/time';
import { setTimeTracking } from '../redux/slices/ui';
import RunningTimer from '../components/RunningTimer';

const TimerDisplay = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
}));

const TimerValue = styled(Typography)(({ theme, isrunning }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h2.fontSize,
    fontFamily: 'monospace',
    color: isrunning === 'true' ? theme.palette.success.main : theme.palette.text.primary,
    lineHeight: 1,
}));

const TaskInfo = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
    width: '100%',
}));

const TaskName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
}));

const ProjectName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(Timer)(({ theme }) => ({
    fontSize: 60,
    marginBottom: theme.spacing(2),
    opacity: 0.3,
}));

const RunningTimerWidget = ({ widgetId, config = {}, onRemove }) => {
    const isRunning = useSelector(state => state.ui.timeTracking);
    const dispatch = useDispatch();

    const { data: timer, isLoading, error, mutate } = useFetchQueue(widgetId, {
        apiCall: () => requestGetRunningTimer(),
        cacheKey: 'running-timer-my',
        cacheTtl: 15000
    });

    // Only the first resolved fetch may auto-enable tracking; socket-driven stops
    // are authoritative and must not be overridden by a later stale cache read.
    const initialAutoEnableDoneRef = useRef(false);
    useEffect(() => {
        if (initialAutoEnableDoneRef.current || isLoading) return;
        if (timer?.startedAt && !isRunning) {
            dispatch(setTimeTracking(true));
        }
        initialAutoEnableDoneRef.current = true;
    }, [timer, isLoading, isRunning, dispatch]);

    useSocketEvent('myTimer:started', (payload) => {
        mutate(() => payload);
        dispatch(setTimeTracking(true));
    });

    useSocketEvent('myTimer:stopped', () => {
        initialAutoEnableDoneRef.current = true;
        mutate(() => null);
        dispatch(setTimeTracking(false));
    });

    return (
        <BaseWidget
            title="My Timer"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!isRunning ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body1" gutterBottom>
                        No timer running
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Start a timer from a task to track your time
                    </Typography>
                </EmptyState>
            ) : (
                <TimerDisplay>
                    <TimerValue isrunning={isRunning.toString()}>
                        <RunningTimer startTime={timer?.startedAt} />
                    </TimerValue>
                    
                    {timer?.parentId && (
                        <TaskInfo>
                            <TaskName>{timer?.parentType === 'Task' ? timer?.task?.name : timer?.project?.name || 'Unknown Task'}</TaskName>
                            <ProjectName>{timer?.project?.name}</ProjectName>
                        </TaskInfo>
                    )}
                </TimerDisplay>
            )}
        </BaseWidget>
    );
};

export default RunningTimerWidget;
